import type { Schemas } from "./types";
import {
  backendFetchResponse,
  DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
  parseResponse,
  type BackendRequestInit,
} from "./transport";

export type Workspace = Schemas["HermesInstanceResponse"];
export type WorkspaceOperation = Schemas["WorkspaceOperation"];

export type WorkspaceClientOptions = {
  /** API root, including its deployment prefix. Browser callers use /api/observatory. */
  baseUrl: string;
  /** Desktop callers supply a fetch that attaches their bearer credential. */
  fetch?: typeof fetch;
};

/** The same platform endpoints serve website sessions and authenticated standalone clients. */
export class WorkspaceClient {
  private readonly fetch: typeof fetch;
  private readonly baseUrl: string;

  constructor(options: WorkspaceClientOptions) {
    this.fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
  }

  private request(
    path: string,
    init: BackendRequestInit = {},
  ): Promise<Response> {
    return backendFetchResponse(
      this.baseUrl,
      path,
      {
        credentials: "same-origin",
        ...init,
      },
      this.fetch,
    );
  }

  async current(signal?: AbortSignal): Promise<Workspace | null> {
    return parseResponse(
      await this.request("/v2/hermes", { signal: signal ?? null }),
    );
  }

  async players(signal?: AbortSignal): Promise<Schemas["PlayerResponse"][]> {
    return parseResponse(
      await this.request("/players", { signal: signal ?? null }),
    );
  }

  async start(playerId: string, signal?: AbortSignal): Promise<Workspace> {
    await parseResponse(
      await this.request("/v2/cogents", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ player_id: playerId }),
        signal: signal ?? null,
      }),
    );
    return parseResponse(
      await this.request(`/v2/cogents/${encodeURIComponent(playerId)}/hermes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: "{}",
        signal: signal ?? null,
      }),
    );
  }

  /** Reuse the owner's workspace, creating or resuming it on an explicit user action. */
  async ensureRunning(signal?: AbortSignal): Promise<Workspace> {
    const workspace = await this.current(signal);

    if (!workspace) {
      const players = (await this.players(signal)).filter(
        (player) => !player.disabled_at,
      );
      const player =
        players.find((candidate) => candidate.is_default) ?? players[0];

      if (!player) throw new Error("Your account has no active player.");

      return this.start(player.id, signal);
    }

    return ["stopped", "stopping", "suspended"].includes(workspace.status)
      ? this.setLifecycle(workspace, "running", signal)
      : workspace;
  }

  /** Wake idle compute without overriding an owner pause or maintenance hold. */
  async wake(
    workspace: Workspace,
    signal?: AbortSignal,
  ): Promise<Schemas["HermesWakeResponse"]> {
    return parseResponse(
      await this.request(
        `/v2/cogents/${encodeURIComponent(workspace.player_id)}/hermes/wake`,
        {
          method: "POST",
          signal: signal ?? null,
        },
      ),
    );
  }

  async setLifecycle(
    workspace: Workspace,
    desiredState: "running" | "stopped",
    signal?: AbortSignal,
  ): Promise<Workspace> {
    return parseResponse(
      await this.request(
        `/v2/cogents/${encodeURIComponent(workspace.player_id)}/hermes/lifecycle`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ desired_state: desiredState }),
          signal: signal ?? null,
        },
      ),
    );
  }

  async reset(
    workspace: Workspace,
    signal?: AbortSignal,
  ): Promise<Schemas["HermesResetResponse"]> {
    return parseResponse(
      await this.request(
        `/v2/cogents/${encodeURIComponent(workspace.player_id)}/hermes/${encodeURIComponent(workspace.provisioning_id)}`,
        {
          method: "DELETE",
          signal: signal ?? null,
        },
      ),
    );
  }

  /** Return the response untouched: relay refusals, bridge results, and event streams have distinct contracts. */
  operation(
    workspaceId: string,
    operation: WorkspaceOperation,
    options: { signal?: AbortSignal | undefined; accept?: string } = {},
  ): Promise<Response> {
    return this.request(
      `/v2/workspaces/${encodeURIComponent(workspaceId)}/ops`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: options.accept ?? "application/json",
        },
        body: JSON.stringify(operation),
        signal: options.signal ?? null,
        // Bridge callers own long operations and event streams through their signal.
        timeoutMs: options.signal ? null : DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
      },
    );
  }
}
